/**
 * Respuestas de la AUDITORÍA TÉCNICA del paquete del 08/10/2026 (VANGUARD_RESPUESTAS_72_Y_NPC, resumen del mensaje del staff).
 *
 * - Procedencia propia: NO son confirmaciones de administración (adminConfirmations) ni pruebas en el juego (inGameTests).
 * - Comprueban la copia local de los 47 CSV (filas, bytes, SHA256 y columnas = 00_control.csv); no prueban
 *   la configuración desplegada hoy ni el comportamiento dentro del juego.
 * - Los CSV siguen siendo la fuente de datos: aquí solo se explica cómo leerlos y qué falta realmente.
 *
 * Efecto en «Pendientes de la guía», por clave original:
 * - resolved: deja de ser pregunta; pasa a «Resuelto con la auditoría técnica» (con el error corregido).
 * - narrowed: REEMPLAZA «Se sabe», «Falta» y la pregunta, para que los tres coincidan.
 */
export const TECHNICAL_AUDIT_LABEL = 'Auditoría técnica del paquete · 08/10/2026'

type Ev = 'administración' | 'captura' | 'configuración' | 'prueba en el juego'

export type TechnicalAnswer =
  | { kind: 'resolved'; error: string; summary: string }
  | { kind: 'narrowed'; known: string; missing: string; question: string; evidence?: Ev[]; /** Conserva además el detalle calculado (p. ej. la lista de mapas). */ appendComputed?: boolean }

/** Exportación nativa de NPC pendiente (solicitud NPC_ORIGEN). */
export const NPC_ORIGIN_KEY = 'source:npc-origen'
/** Tiendas y servicios (solicitud TIENDAS_SERVICIOS). */
export const SHOPS_SOURCE_KEY = 'source:tiendas-servicios'
/** Una sola solicitud de archivos para los sistemas (cada sistema conserva su propia verificación). */
export const SYSTEMS_SOURCE_KEY = 'source:sistemas'
/** Una sola solicitud técnica: configuración/código del servidor (mezclas, efectos de misiones, combo, posiciones en eventos). */
export const SERVER_SOURCE_KEY = 'source:servidor'
/** Pregunta única COORDENADAS: posiciones, imágenes y calibración con estados separados. */
export const COORD_KEY = 'coordenadas'

export const technicalAnswers: Record<string, TechnicalAnswer> = {
  'invasion:golden': {
    kind: 'resolved',
    error: 'La guía contaba como conflicto los posibles premios de las cajas Kundun, como si el dorado los soltara directamente.',
    summary:
      'Los 20 recorridos directos entregan Box of Kundun 14,11@8…@12; las 382 rutas restantes son posibles premios al abrir esas cajas. Se presenta «Monstruo dorado → Box of Kundun → posible premio».',
  },
  'invasion:white-wizard': {
    kind: 'resolved',
    error: 'La guía trataba quality vacío como «normal» y marcaba conflictos de calidad que no existen.',
    summary:
      'Bolsa 27 → variantes 376/377 → perfil 4:12 (excellent_count = 1). El grupo 3 se activa con 2000/10000; la distribución del perfil es condicional y no es la probabilidad final por muerte.',
  },
  'invasion:red-dragon': {
    kind: 'narrowed',
    known: 'En la exportación del 08/10/2026, la bag 2 del Red Dragon contiene Dragon Helm (7,1). Eso difiere de la instrucción del staff. La evidencia se conserva y no afecta los drops normales de Dragon Helm en otros monstruos.',
    missing: 'Si la bag 2 desplegada hoy incluye Dragon Helm (7,1), o la diferencia exacta',
    question: 'La bag 2 del Red Dragon incluye Dragon Helm (7,1) en la exportación del 08/10/2026, distinto de lo indicado por el staff. ¿Cuál vale hoy en el servidor? Si cambió, ¿qué fila exacta?',
    evidence: ['administración', 'configuración'],
  },
  'invasion:death-king': {
    kind: 'narrowed',
    known: 'Las listas de joyas y equipo del Death King están exportadas; calidad y opciones se leen de sus perfiles, no de un campo vacío.',
    missing: 'Solo vigencia o diferencias concretas respecto a la exportación',
    question: '¿La recompensa del Death King exportada el 08/10/2026 sigue vigente? Si no, ¿qué ítems cambiaron?',
    evidence: ['administración'],
  },
  'map-access:none': {
    kind: 'narrowed',
    known:
      'Cruzado 07_maps con 33_event_rooms: 28 reglas de salas para 23 mapas sin /move. El nivel de una sala no es el mecanismo de entrada. Devil Square y Chaos Castle están habilitados como eventos (27_events.active_configured = True, en servermain1 y servermain2). Por sala: Devil Square 1–6 activas y 7 deshabilitada; Chaos Castle 1–5 y 7 activas y 6 con rango 851–100 inválido (no se corrige a un máximo supuesto). Una sala no desactiva el evento; Barracks/Refuge no son acceso libre a nivel 1 por una celda minimum_move_level sin move_entry.',
    missing: 'Mecanismo de entrada de los mapas sin /move (portal, NPC, ticket o evento) y el rango correcto de Chaos Castle 6',
    question: 'Para los mapas sin /move (incluidos Barracks y Refuge), ¿cómo se entra en Vanguard y con qué requisito? ¿Qué rango de nivel tiene Chaos Castle 6?',
    evidence: ['administración', 'configuración'],
    appendComputed: true,
  },
  'combo:bk': {
    kind: 'narrowed',
    known: 'Requisitos de la misión 3 en 37/38. Regla de clase: ChangeUp+1 ≥ requisito; el valor 2 no demuestra que el combo deje de funcionar al pasar a Blade Master.',
    missing: 'Secuencia de habilidades y tiempos del combo',
    question: '¿Qué secuencia de habilidades activa el combo en Vanguard y con qué tiempos?',
    evidence: ['administración', 'prueba en el juego'],
  },
}

export const technicalAnswer = (key: string): TechnicalAnswer | undefined => technicalAnswers[key]

/** Texto público corto para fichas de NPC sin ubicación (sin publicar el informe técnico completo). */
export const NPC_LOCATION_GAP =
  'La exportación del 08/10/2026 no incluye ubicaciones de NPC. Falta: mapa y coordenadas X/Y.'
export const SHOP_INVENTORY_GAP = 'Inventario de tienda pendiente de documentar.'
